import { IFlashcardPracticeFile, ITranscribeAnswer } from '@sdk/contracts';
import { useMutation } from '@tanstack/react-query';
import { message } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';

import { FlashcardsPracticeTranslations } from '@/pages/Student/FlashcardsPractice/i18n';
import { sdk } from '@/services';
import { convertStorageLanguageToText } from '@/utils';

const FlashcardPracticeService = sdk.getService('FlashcardPracticeService');

interface IStore {
  transcription: string;
  isTranscribing: boolean;
  language: string;
  setTranscription: (value: string) => void;
  setIsTranscribing: (value: boolean) => void;
  setLanguage: (value: string) => void;
}

const normalizeLanguage = (lang: string | null): string => {
  const allowedLanguages = ['es', 'en', 'pt', 'ca'];
  if (!lang) return 'es';

  if (allowedLanguages.includes(lang)) return lang;
  const langLower = lang.toLowerCase();
  if (langLower.startsWith('es')) return 'es';
  if (langLower.startsWith('en')) return 'en';
  if (langLower.startsWith('pt')) return 'pt';
  if (langLower.startsWith('ca')) return 'ca';

  return 'es';
};

const store = create<IStore>(set => ({
  transcription: '',
  isTranscribing: false,
  language: normalizeLanguage(localStorage.getItem('i18nextLng')),
  setTranscription: (value: string) => set({ transcription: value }),
  setIsTranscribing: (value: boolean) => set({ isTranscribing: value }),
  setLanguage: (value: string) => set({ language: value })
}));

export const useMutateFlashcardPracticeTranscribeAnswer = () => {
  const { t } = useTranslation('FlashcardsPracticeTranslations');
  const {
    transcription,
    setTranscription,
    setIsTranscribing,
    isTranscribing,
    language,
    setLanguage
  } = store();
  const { mutate: transcribeAnswer, isPending } = useMutation<
    ITranscribeAnswer,
    Error,
    IFlashcardPracticeFile
  >({
    mutationFn: async ({ file }) => {
      setTranscription('');
      await new Promise(resolve => setTimeout(resolve, 2000));
      return FlashcardPracticeService.transcribeAnswer({ file, language });
    },

    onSuccess: result => {
      message.success(t('TRANSCRIBED_SUCCESSFULLY'));
      setTranscription(result.transcription);
    },
    onError: () => {
      message.error(t('TRANSCRIPTION_FAILED'));
    }
  });

  const languageText = useMemo(() => {
    return t(convertStorageLanguageToText(language) as keyof typeof FlashcardsPracticeTranslations);
  }, [language]);

  useEffect(() => {
    setIsTranscribing(isPending);
  }, [isPending]);

  return {
    transcribeAnswer,
    isTranscribing,
    transcription,
    setTranscription,
    language: languageText,
    setLanguage
  };
};
